import React, { useState, useEffect, useCallback } from 'react';
import { Package, CreditCard, FileText, Plus, Coins, CheckCircle2, Clock, XCircle, Wallet, MapPin, Navigation, Star, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import StatCard from '@/components/dashboard/StatCard';
import AvaliacaoEntrega from '@/components/AvaliacaoEntrega';
import MapaEntrega from '@/components/MapaEntrega';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { criarEntrega, listarEntregas, criarPagamento, simularAprovacao, obterEmpresa } from '@/services/api';
import api from '@/services/api';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const DashboardCliente = () => {
  const { empresa, atualizarEmpresa } = useAuth();
  const navigate = useNavigate();
  const [entregas, setEntregas] = useState([]);
  const [entregadores, setEntregadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNovaEntrega, setOpenNovaEntrega] = useState(false);
  const [openPagamento, setOpenPagamento] = useState(false);
  const [openDetalhes, setOpenDetalhes] = useState(false);
  const [entregaSelecionada, setEntregaSelecionada] = useState(null);
  const [entregaParaAvaliar, setEntregaParaAvaliar] = useState(null);
  const [entregaParaRastrear, setEntregaParaRastrear] = useState(null);
  const [tipoPagamento, setTipoPagamento] = useState('creditos');
  const [quantidadeCreditos, setQuantidadeCreditos] = useState(10);
  const [pagamentoAtual, setPagamentoAtual] = useState(null);
  
  // Autocomplete de destino
  const [destinoInput, setDestinoInput] = useState('');
  const [destinoNumero, setDestinoNumero] = useState('');
  const [destinoComplemento, setDestinoComplemento] = useState('');
  const [destinoSugestoes, setDestinoSugestoes] = useState([]);
  const [destinoSelecionado, setDestinoSelecionado] = useState(null);
  const [loadingSugestoes, setLoadingSugestoes] = useState(false);
  
  const debouncedDestino = useDebounce(destinoInput, 300);
  
  const [novaEntrega, setNovaEntrega] = useState({
    descricao: '',
    observacoes: '',
    valor_recebimento: '',
    tipo_cobranca: 'recebido',
    entregador_id: '',
    modo_atribuicao: 'aberta',
    forma_pagamento: 'dinheiro'
  });
  
  // Busca sugestões de endereço
  useEffect(() => {
    const buscarSugestoes = async () => {
      if (debouncedDestino.length < 3) {
        setDestinoSugestoes([]);
        return;
      }
      
      setLoadingSugestoes(true);
      try {
        const response = await api.get('/geocode/autocomplete', {
          params: { q: debouncedDestino, limit: 5 }
        });
        setDestinoSugestoes(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar sugestões:', error);
        setDestinoSugestoes([]);
      } finally {
        setLoadingSugestoes(false);
      }
    };
    
    buscarSugestoes();
  }, [debouncedDestino]);
  
  useEffect(() => {
    carregarEntregas();
    carregarEntregadores();
    atualizarDadosEmpresa();
  }, []);
  
  const atualizarDadosEmpresa = async () => {
    try {
      const response = await obterEmpresa(empresa.id);
      atualizarEmpresa(response.data);
    } catch (error) {
      console.error('Erro ao atualizar empresa:', error);
    }
  };
  
  const carregarEntregas = async () => {
    try {
      const response = await listarEntregas({ cliente_id: empresa.id });
      setEntregas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar entregas');
    }
  };
  
  const carregarEntregadores = async () => {
    try {
      const response = await api.get('/entregadores/ativos');
      console.log('Entregadores carregados:', response.data);
      setEntregadores(response.data);
      if (response.data.length === 0) {
        toast.info('Nenhum entregador ativo encontrado.');
      }
    } catch (error) {
      console.error('Erro ao carregar entregadores:', error);
      toast.error('Erro ao carregar lista de entregadores');
    }
  };
  
  const handleCriarEntrega = async (e) => {
    e.preventDefault();
    
    if (!empresa.assinatura_ativa) {
      toast.error('Ative sua assinatura para criar entregas!');
      return;
    }
    
    if (empresa.creditos_disponiveis < 1) {
      toast.error('Créditos insuficientes! Compre créditos para continuar.');
      return;
    }
    
    // Valida endereço de origem
    if (!empresa.endereco_latitude || !empresa.endereco_longitude) {
      toast.error('Cadastre o endereço da sua empresa primeiro!');
      navigate('/dashboard/cliente/endereco');
      return;
    }
    
    // Valida endereço de destino
    if (!destinoSelecionado) {
      toast.error('Selecione um endereço de destino válido!');
      return;
    }
    
    // Valida número da residência
    if (!destinoNumero || !destinoNumero.trim()) {
      toast.error('Informe o número da residência/estabelecimento!');
      return;
    }
    
    setLoading(true);
    
    try {
      // Concatena o número (e complemento) ao endereço selecionado
      // Geoapify pode retornar a rua sem número; aqui garantimos que ele entra
      const partes = destinoSelecionado.endereco.split(',');
      let enderecoComNumero;
      if (partes.length > 1) {
        // Insere o número logo após a rua (primeira parte)
        enderecoComNumero = `${partes[0].trim()}, ${destinoNumero.trim()}${destinoComplemento.trim() ? ' - ' + destinoComplemento.trim() : ''},${partes.slice(1).join(',')}`;
      } else {
        enderecoComNumero = `${destinoSelecionado.endereco}, ${destinoNumero.trim()}${destinoComplemento.trim() ? ' - ' + destinoComplemento.trim() : ''}`;
      }
      
      const dados = {
        ...novaEntrega,
        cliente_id: empresa.id,
        destino_endereco: enderecoComNumero,
        destino_latitude: destinoSelecionado.latitude,
        destino_longitude: destinoSelecionado.longitude,
        valor_recebimento: novaEntrega.valor_recebimento ? parseFloat(novaEntrega.valor_recebimento) : null
      };
      
      await criarEntrega(dados);
      toast.success('Entrega criada com sucesso! Pode cadastrar outra.');
      
      // NÃO fecha o modal - permite criar múltiplas entregas
      // setOpenNovaEntrega(false);
      
      // Limpa apenas os campos, mantém entregador selecionado
      setNovaEntrega({
        ...novaEntrega,
        descricao: '',
        observacoes: '',
        valor_recebimento: '',
        tipo_cobranca: 'recebido',
        forma_pagamento: 'dinheiro'
      });
      setDestinoNumero('');
      setDestinoComplemento('');
      
      // Limpa destino
      setDestinoInput('');
      setDestinoSelecionado(null);
      setDestinoSugestoes([]);
      
      carregarEntregas();
      atualizarDadosEmpresa();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar entrega');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCriarPagamento = async () => {
    setLoading(true);
    
    try {
      const dados = {
        empresa_id: empresa.id,
        tipo: tipoPagamento,
        metodo: 'pix',
        quantidade_creditos: tipoPagamento === 'creditos' ? quantidadeCreditos : null
      };
      
      const response = await criarPagamento(dados);
      setPagamentoAtual(response.data);
      toast.success('Pagamento criado! Aguardando aprovação...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pagamento');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSimularAprovacao = async () => {
    if (!pagamentoAtual) return;
    
    setLoading(true);
    try {
      await simularAprovacao(pagamentoAtual.id);
      toast.success('Pagamento aprovado! Acesso liberado.');
      
      setPagamentoAtual(null);
      setOpenPagamento(false);
      atualizarDadosEmpresa();
    } catch (error) {
      toast.error('Erro ao aprovar pagamento');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReenviarEntrega = async (entrega) => {
    if (!window.confirm('Reenviar este pedido para todos os entregadores disponíveis?')) {
      return;
    }
    try {
      await api.post(`/entregas/${entrega.id}/reenviar`, { cliente_id: empresa.id });
      toast.success('Pedido reenviado! Entregadores foram notificados.');
      carregarEntregas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reenviar pedido');
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      criada: { label: 'Criada', color: 'bg-slate-100 text-slate-800' },
      atribuida: { label: 'Atribuída', color: 'bg-blue-100 text-blue-800' },
      aceita: { label: 'Aceita', color: 'bg-emerald-100 text-emerald-800' },
      em_rota: { label: 'Em Rota', color: 'bg-amber-100 text-amber-800' },
      entregue: { label: 'Finalizado ✓', color: 'bg-green-100 text-green-800 font-bold' },
      cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-800' }
    };
    
    const badge = badges[status] || badges.criada;
    return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.color}`}>{badge.label}</span>;
  };
  
  return (
    <DashboardLayout title="Dashboard Cliente">
      {/* Alertas de Validação */}
      {empresa.status_validacao !== 'aprovado' && (
        <div data-testid="validation-alert" className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800 font-semibold">
            Validação Pendente - Envie seus documentos para liberar seu cadastro.
          </p>
          <Button 
            size="sm" 
            className="mt-2 bg-amber-600 hover:bg-amber-700"
            onClick={() => navigate('/dashboard/cliente/documentos')}
          >
            <FileText className="w-4 h-4 mr-2" />
            Enviar Documentos
          </Button>
        </div>
      )}
      
      {/* Alertas */}
      {!empresa.assinatura_ativa && (
        <div data-testid="subscription-alert" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800 font-semibold">
            Assinatura Inativa - Ative sua assinatura para criar entregas!
          </p>
        </div>
      )}
      
      {empresa.assinatura_ativa && empresa.creditos_disponiveis < 5 && (
        <div data-testid="credits-warning" className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800 font-semibold">
            Créditos Baixos - Você tem apenas {empresa.creditos_disponiveis} créditos disponíveis.
          </p>
        </div>
      )}
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Créditos Disponíveis"
          value={empresa.creditos_disponiveis || 0}
          subtitle={`Total: ${empresa.creditos_total || 0} | Usados: ${empresa.creditos_utilizados || 0}`}
          icon={Coins}
          color="emerald"
        />
        <StatCard
          title="Entregas"
          value={entregas.length}
          subtitle="Total de entregas"
          icon={Package}
          color="blue"
        />
        <StatCard
          title="Entregues"
          value={entregas.filter(e => e.status === 'entregue').length}
          icon={CheckCircle2}
          color="emerald"
        />
        <StatCard
          title="Em Andamento"
          value={entregas.filter(e => ['aceita', 'em_rota'].includes(e.status)).length}
          icon={Clock}
          color="amber"
        />
      </div>
      
      {/* Ações */}
      <div className="flex gap-4 mb-6">
        <Dialog open={openNovaEntrega} onOpenChange={setOpenNovaEntrega}>
          <DialogTrigger asChild>
            <Button data-testid="create-delivery-button" className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              Nova Entrega
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader className="pb-2">
              <DialogTitle>Nova Entrega</DialogTitle>
              <p className="text-xs text-slate-500">Preencha os dados. O modal permanece aberto para cadastrar várias entregas.</p>
            </DialogHeader>
            <form onSubmit={handleCriarEntrega} className="space-y-3">
              {/* Modo de Atribuição */}
              <div>
                <label className="text-sm font-medium block mb-2">Modo de Atribuição</label>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50 ${novaEntrega.modo_atribuicao === 'direcionada' ? 'border-orange-500 bg-orange-50' : ''}`}>
                    <input
                      type="radio"
                      name="modo_atribuicao"
                      value="direcionada"
                      checked={novaEntrega.modo_atribuicao === 'direcionada'}
                      onChange={(e) => setNovaEntrega({...novaEntrega, modo_atribuicao: e.target.value})}
                      className="w-4 h-4 accent-orange-600"
                    />
                    <div>
                      <p className="font-medium text-sm">Direcionada</p>
                      <p className="text-xs text-slate-500">Escolher entregador</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50 ${novaEntrega.modo_atribuicao === 'aberta' ? 'border-orange-500 bg-orange-50' : ''}`}>
                    <input
                      type="radio"
                      name="modo_atribuicao"
                      value="aberta"
                      checked={novaEntrega.modo_atribuicao === 'aberta'}
                      onChange={(e) => setNovaEntrega({...novaEntrega, modo_atribuicao: e.target.value, entregador_id: ''})}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium text-sm">Aberta</p>
                      <p className="text-xs text-slate-500">Qualquer entregador</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Seleção de Entregador (apenas para modo direcionado) */}
              {novaEntrega.modo_atribuicao === 'direcionada' && (
                <div>
                  <label className="text-sm font-medium">Entregador *</label>
                  <select
                    data-testid="entregador-select"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    value={novaEntrega.entregador_id}
                    onChange={(e) => setNovaEntrega({...novaEntrega, entregador_id: e.target.value})}
                    required
                  >
                    <option value="">Selecione o entregador</option>
                    {entregadores.map((ent) => (
                      <option key={ent.id} value={ent.id}>
                        {ent.nome_fantasia || ent.razao_social}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Descrição *</label>
                  <Input
                    data-testid="delivery-description-input"
                    value={novaEntrega.descricao}
                    onChange={(e) => setNovaEntrega({...novaEntrega, descricao: e.target.value})}
                    placeholder="Ex: Entrega de documentos"
                    required
                    className="h-9 text-sm"
                  />
                </div>
              </div>
              
              {/* Endereço de Origem (fixo da empresa) */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-orange-600" />
                  <label className="text-sm font-medium text-slate-700">Origem (Endereço da Empresa)</label>
                </div>
                {empresa.endereco_completo ? (
                  <p className="text-sm text-slate-600 ml-6">{empresa.endereco_completo}</p>
                ) : (
                  <div className="ml-6">
                    <p className="text-sm text-amber-600">Endereço não cadastrado</p>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      className="mt-2"
                      onClick={() => navigate('/dashboard/cliente/endereco')}
                    >
                      Cadastrar Endereço
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Endereço de Destino (autocomplete) */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Navigation className="w-4 h-4 text-green-600" />
                  <label className="text-sm font-medium">Endereço de Destino *</label>
                </div>
                <div className="relative">
                  <Input
                    data-testid="delivery-destination-input"
                    value={destinoInput}
                    onChange={(e) => {
                      setDestinoInput(e.target.value);
                      setDestinoSelecionado(null);
                    }}
                    placeholder="Digite o endereço de destino..."
                    className="h-10 text-sm"
                  />
                  {loadingSugestoes && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  
                  {/* Sugestões de autocomplete */}
                  {destinoSugestoes.length > 0 && !destinoSelecionado && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {destinoSugestoes.map((sugestao, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-orange-50 border-b last:border-b-0 flex items-start gap-2"
                          onClick={() => {
                            setDestinoSelecionado(sugestao);
                            setDestinoInput(sugestao.endereco);
                            setDestinoSugestoes([]);
                          }}
                        >
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{sugestao.endereco}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {destinoSelecionado && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Endereço selecionado com coordenadas
                  </p>
                )}
              </div>
              
              {/* Número e Complemento do destino */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-sm font-medium">Número *</label>
                  <Input
                    data-testid="delivery-numero-input"
                    value={destinoNumero}
                    onChange={(e) => setDestinoNumero(e.target.value)}
                    placeholder="Ex: 123"
                    className="h-10 text-sm"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium">Complemento</label>
                  <Input
                    data-testid="delivery-complemento-input"
                    value={destinoComplemento}
                    onChange={(e) => setDestinoComplemento(e.target.value)}
                    placeholder="Apto 42, Bloco B, casa fundos..."
                    className="h-10 text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Observações</label>
                <Textarea
                  value={novaEntrega.observacoes}
                  onChange={(e) => setNovaEntrega({...novaEntrega, observacoes: e.target.value})}
                  className="text-sm h-16"
                />
              </div>
              
              <div className="border-t pt-3">
                <label className="text-sm font-medium block mb-2">Cobrança na Entrega</label>
                
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="tipo_cobranca"
                      value="recebido"
                      checked={novaEntrega.tipo_cobranca === 'recebido'}
                      onChange={(e) => setNovaEntrega({...novaEntrega, tipo_cobranca: e.target.value, valor_recebimento: ''})}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Já Recebido</p>
                      <p className="text-xs text-slate-600">Pagamento já realizado</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="tipo_cobranca"
                      value="a_receber"
                      checked={novaEntrega.tipo_cobranca === 'a_receber'}
                      onChange={(e) => setNovaEntrega({...novaEntrega, tipo_cobranca: e.target.value})}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">A Receber</p>
                      <p className="text-xs text-slate-600">Cobrar na entrega</p>
                    </div>
                  </label>
                </div>
                
                {novaEntrega.tipo_cobranca === 'a_receber' && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="text-sm font-medium">Valor a Receber *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={novaEntrega.valor_recebimento}
                        onChange={(e) => setNovaEntrega({...novaEntrega, valor_recebimento: e.target.value})}
                        placeholder="R$ 0,00"
                        required
                        className="h-9 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium block mb-2">Forma de Pagamento</label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className={`flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50 ${novaEntrega.forma_pagamento === 'dinheiro' ? 'border-green-500 bg-green-50' : ''}`}>
                          <input
                            type="radio"
                            name="forma_pagamento"
                            value="dinheiro"
                            checked={novaEntrega.forma_pagamento === 'dinheiro'}
                            onChange={(e) => setNovaEntrega({...novaEntrega, forma_pagamento: e.target.value})}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Dinheiro</span>
                        </label>
                        <label className={`flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50 ${novaEntrega.forma_pagamento === 'cartao_simulado' ? 'border-green-500 bg-green-50' : ''}`}>
                          <input
                            type="radio"
                            name="forma_pagamento"
                            value="cartao_simulado"
                            checked={novaEntrega.forma_pagamento === 'cartao_simulado'}
                            onChange={(e) => setNovaEntrega({...novaEntrega, forma_pagamento: e.target.value})}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Cartão (Simulado)</span>
                        </label>
                      </div>
                    </div>
                    
                    <p className="text-xs text-amber-600">
                      Será adicionado aos recebíveis (registro informativo)
                    </p>
                  </div>
                )}
              </div>
              
              <div className="bg-orange-50 p-2 rounded">
                <p className="text-xs text-orange-800">1 crédito (R$ 1,50) será consumido</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setOpenNovaEntrega(false);
                    setNovaEntrega({
                      descricao: '',
                      observacoes: '',
                      valor_recebimento: '',
                      tipo_cobranca: 'recebido',
                      entregador_id: '',
                      modo_atribuicao: 'direcionada',
                      forma_pagamento: 'dinheiro'
                    });
                    setDestinoInput('');
                    setDestinoSelecionado(null);
                    setDestinoSugestoes([]);
                  }}
                  className="flex-1"
                >
                  Fechar
                </Button>
                <Button 
                  data-testid="submit-delivery-button" 
                  type="submit" 
                  disabled={loading || !destinoSelecionado || !destinoNumero.trim() || !empresa.endereco_latitude || (novaEntrega.modo_atribuicao === 'direcionada' && !novaEntrega.entregador_id)} 
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  {loading ? 'Criando...' : 'Criar Entrega'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        
        <Dialog open={openPagamento} onOpenChange={setOpenPagamento}>
          <DialogTrigger asChild>
            <Button data-testid="payment-button" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
              <CreditCard className="w-4 h-4 mr-2" />
              Pagamentos
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pagamentos</DialogTitle>
            </DialogHeader>
            
            {!pagamentoAtual ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Tipo de Pagamento</label>
                  <select
                    data-testid="payment-type-select"
                    className="w-full border rounded-md p-2"
                    value={tipoPagamento}
                    onChange={(e) => setTipoPagamento(e.target.value)}
                  >
                    <option value="assinatura">Assinatura Mensal (R$ 149,90)</option>
                    <option value="creditos">Comprar Créditos</option>
                  </select>
                </div>
                
                {tipoPagamento === 'creditos' && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Quantidade de Créditos</label>
                    <Input
                      data-testid="credits-quantity-input"
                      type="number"
                      min="1"
                      value={quantidadeCreditos}
                      onChange={(e) => setQuantidadeCreditos(parseInt(e.target.value))}
                    />
                    <p className="text-sm text-slate-600 mt-1">
                      Total: R$ {(quantidadeCreditos * 1.5).toFixed(2)}
                    </p>
                  </div>
                )}
                
                <Button
                  data-testid="create-payment-button"
                  onClick={handleCriarPagamento}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Criando...' : 'Gerar PIX'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded text-center">
                  <p className="text-sm text-slate-600 mb-2">Pagamento PIX Gerado</p>
                  <p className="text-2xl font-bold text-slate-900">
                    R$ {pagamentoAtual.valor?.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">Status: {pagamentoAtual.status}</p>
                </div>
                
                {pagamentoAtual.pix_qr_code && (
                  <div className="bg-slate-100 p-3 rounded">
                    <p className="text-xs text-slate-600 break-all font-mono">
                      {pagamentoAtual.pix_qr_code}
                    </p>
                  </div>
                )}
                
                <div className="bg-amber-50 border border-amber-200 p-3 rounded">
                  <p className="text-sm text-amber-800">
                    <strong>Modo Desenvolvimento:</strong><br />
                    Simule a aprovação do pagamento clicando no botão abaixo.
                  </p>
                </div>
                
                <Button
                  data-testid="simulate-approval-button"
                  onClick={handleSimularAprovacao}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? 'Processando...' : 'Simular Aprovação (Dev)'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        <Button
          data-testid="financial-button"
          variant="outline"
          onClick={() => navigate('/dashboard/cliente/financeiro')}
          className="border-blue-600 text-blue-600 hover:bg-blue-50"
        >
          <Wallet className="w-4 h-4 mr-2" />
          Controle Financeiro
        </Button>
      </div>
      
      {/* Lista de Entregas */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Entregas
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Destino</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entregas.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                    Nenhuma entrega cadastrada
                  </td>
                </tr>
              ) : (
                entregas.map((entrega) => (
                  <tr key={entrega.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-mono text-slate-600">
                      {entrega.id.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900">{entrega.descricao}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{entrega.destino_endereco}</td>
                    <td className="px-6 py-4">{getStatusBadge(entrega.status)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(entrega.criado_em).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEntregaSelecionada(entrega);
                            setOpenDetalhes(true);
                          }}
                        >
                          Detalhes
                        </Button>
                        {['aceita', 'atribuida', 'em_rota'].includes(entrega.status) && (
                          <Button
                            size="sm"
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                            onClick={() => setEntregaParaRastrear(entrega)}
                            data-testid={`btn-rastrear-${entrega.id}`}
                          >
                            <Navigation className="w-4 h-4 mr-1" />
                            Acompanhar
                          </Button>
                        )}
                        {['criada', 'atribuida'].includes(entrega.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-500 text-amber-700 hover:bg-amber-50"
                            onClick={() => handleReenviarEntrega(entrega)}
                            data-testid={`btn-reenviar-${entrega.id}`}
                            title="Reenviar pedido para os entregadores"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Reenviar
                          </Button>
                        )}
                        {entrega.status === 'entregue' && !entrega.avaliada && (
                          <Button
                            size="sm"
                            className="bg-yellow-500 hover:bg-yellow-600 text-white"
                            onClick={() => setEntregaParaAvaliar(entrega)}
                            data-testid={`btn-avaliar-${entrega.id}`}
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Avaliar
                          </Button>
                        )}
                        {entrega.status === 'entregue' && entrega.avaliada && (
                          <span 
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-50 rounded-md"
                            data-testid={`avaliacao-feita-${entrega.id}`}
                          >
                            <Star className="w-3 h-3 mr-1 fill-yellow-500 text-yellow-500" />
                            {entrega.avaliacao_estrelas || ''} estrelas
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal Detalhes da Entrega */}
      <Dialog open={openDetalhes} onOpenChange={setOpenDetalhes}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes da Entrega</DialogTitle>
          </DialogHeader>
          
          {entregaSelecionada && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600">Descrição</label>
                <p className="text-slate-900">{entregaSelecionada.descricao}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Origem</label>
                  <p className="text-sm text-slate-900">{entregaSelecionada.origem_endereco}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Destino</label>
                  <p className="text-sm text-slate-900">{entregaSelecionada.destino_endereco}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600">Status</label>
                <div className="mt-1">{getStatusBadge(entregaSelecionada.status)}</div>
              </div>
              
              {entregaSelecionada.codigo_confirmacao && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">🔒</span>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-amber-900 block">
                        Código de Confirmação
                      </label>
                      <p className="text-xs text-amber-700">
                        SIGILOSO - Apenas para o entregador finalizar
                      </p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-4xl font-bold text-amber-900 font-mono tracking-[0.3em]">
                      {entregaSelecionada.codigo_confirmacao}
                    </p>
                  </div>
                  <p className="text-xs text-amber-700 mt-3 text-center">
                    O entregador digitará este código para finalizar a entrega
                  </p>
                  {entregaSelecionada.confirmada_em && (
                    <div className="mt-3 bg-green-100 rounded-lg p-2 text-center">
                      <p className="text-sm text-green-700 font-medium">
                        ✓ Entrega confirmada em {new Date(entregaSelecionada.confirmada_em).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {entregaSelecionada.observacoes && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Observações</label>
                  <p className="text-sm text-slate-900">{entregaSelecionada.observacoes}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-xs font-medium text-slate-500">Criada em</label>
                  <p className="text-slate-900">
                    {new Date(entregaSelecionada.criado_em).toLocaleString('pt-BR')}
                  </p>
                </div>
                {entregaSelecionada.finalizado_em && (
                  <div>
                    <label className="text-xs font-medium text-slate-500">Finalizada em</label>
                    <p className="text-slate-900">
                      {new Date(entregaSelecionada.finalizado_em).toLocaleString('pt-BR')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Avaliação da Entrega */}
      {entregaParaAvaliar && (
        <AvaliacaoEntrega
          entrega={entregaParaAvaliar}
          clienteId={empresa.id}
          onClose={() => setEntregaParaAvaliar(null)}
          onSuccess={() => {
            carregarEntregas();
            setEntregaParaAvaliar(null);
          }}
        />
      )}
      
      {/* Modal de Rastreamento (mapa em tempo real) */}
      {entregaParaRastrear && (
        <MapaEntrega
          entregaId={entregaParaRastrear.id}
          isOpen={!!entregaParaRastrear}
          onClose={() => setEntregaParaRastrear(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default DashboardCliente;
